import { useRef, useState } from 'react';
import { Search, Plus, BookOpen, Pencil, Trash2, Eye, Link2, ExternalLink, Image as ImageIcon } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { useKnowledgeBase, KbArticle, KbArticleSummary } from '../hooks/useKnowledgeBase';
import { useKbImages } from '../hooks/useKbImages';
import { useAuth } from '../hooks/useAuth';
import { hasRole } from '../utils/roleUtils';
import { Card, Modal, EmptyState, SkeletonCard } from '../components/ui';
import Button from '../components/Button';
import KbImage from '../components/kb/KbImage';

const mdComponents = (articleId: string) => ({
  img: ({ src, alt }: any) => <KbImage articleId={articleId} imageId={String(src)} alt={alt} />,
  p:  (p: any) => <p className="mb-2" {...p} />,
  ul: (p: any) => <ul className="list-disc pl-5 mb-2 space-y-1" {...p} />,
  ol: (p: any) => <ol className="list-decimal pl-5 mb-2 space-y-1" {...p} />,
  h1: (p: any) => <h2 className="text-base font-semibold text-navy mt-3 mb-1" {...p} />,
  h2: (p: any) => <h3 className="text-sm font-semibold text-navy mt-3 mb-1" {...p} />,
  h3: (p: any) => <h4 className="text-sm font-semibold text-navy mt-2 mb-1" {...p} />,
  a:  (p: any) => <a className="text-brand-dark underline" target="_blank" rel="noopener noreferrer" {...p} />,
  strong: (p: any) => <strong className="font-semibold" {...p} />,
});

function Editor({ initial, onClose, onCreate, onUpdate }: {
  initial?: KbArticle; onClose: () => void;
  onCreate: (d: Partial<KbArticle>) => Promise<string | null>;
  onUpdate: (id: string, d: Partial<KbArticle>) => Promise<string | null>;
}) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [category, setCategory] = useState(initial?.category ?? '');
  const [resourceType, setResourceType] = useState<'article' | 'link'>(initial?.resourceType ?? 'article');
  const [body, setBody] = useState(initial?.body ?? '');
  const [externalUrl, setExternalUrl] = useState(initial?.externalUrl ?? '');
  const [visibility, setVisibility] = useState(initial?.visibility ?? 'internal');
  const [saving, setSaving] = useState(false);
  const [articleId, setArticleId] = useState(initial?.id);

  const { uploading, upload } = useKbImages(articleId);
  const fileRef = useRef<HTMLInputElement>(null);

  const isLink = resourceType === 'link';
  const validUrl = /^https?:\/\/.+/i.test(externalUrl.trim());
  const canSave = title.trim() && (isLink ? validUrl : body.trim());

  const save = async () => {
    setSaving(true);
    const payload = {
      title, category: category || undefined, resourceType,
      body: isLink ? (body || undefined) : body,
      externalUrl: isLink ? externalUrl.trim() : undefined,
      visibility, isPublished: true,
    };
    const id = articleId ? await onUpdate(articleId, payload) : await onCreate(payload);
    setSaving(false);
    if (!id) return;
    // First save of a brand-new article — stay open so screenshots can be
    // attached (image upload needs an existing articleId). Any later save closes.
    if (!articleId) setArticleId(id);
    else onClose();
  };

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    const image = await upload(f);
    if (image) setBody(b => `${b}\n\n![Screenshot](${image.id})\n`);
  };

  return (
    <Modal open onClose={onClose} size="xl" title={articleId ? 'Edit article' : 'New article'}
      footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={save} disabled={saving || !canSave}>Save</Button></>}>
      <div className="space-y-3">
        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <label className="block text-xs font-medium text-gray-600 mb-1">Title</label>
            <input value={title} onChange={e => setTitle(e.target.value)}
              className="w-full text-sm rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
            <input value={category} onChange={e => setCategory(e.target.value)}
              className="w-full text-sm rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30" />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
          <div className="flex gap-2">
            <button type="button" onClick={() => setResourceType('article')}
              className={`flex-1 flex items-center justify-center gap-1.5 text-sm rounded-lg border px-3 py-2 transition-colors ${
                !isLink ? 'border-brand bg-brand/10 text-brand-dark font-medium' : 'border-gray-300 text-gray-500'}`}>
              <BookOpen size={14} /> Written article
            </button>
            <button type="button" onClick={() => setResourceType('link')}
              className={`flex-1 flex items-center justify-center gap-1.5 text-sm rounded-lg border px-3 py-2 transition-colors ${
                isLink ? 'border-brand bg-brand/10 text-brand-dark font-medium' : 'border-gray-300 text-gray-500'}`}>
              <Link2 size={14} /> External link / video
            </button>
          </div>
        </div>

        {isLink ? (
          <>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">External URL (e.g. video training link)</label>
              <input value={externalUrl} onChange={e => setExternalUrl(e.target.value)} placeholder="https://…"
                className="w-full text-sm rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30" />
              {externalUrl.trim() && !validUrl && (
                <p className="text-xs text-red-500 mt-1">Must start with http:// or https://</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Description (optional)</label>
              <textarea value={body} onChange={e => setBody(e.target.value)} rows={4}
                placeholder="What will the reader find at this link?"
                className="w-full text-sm rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30" />
            </div>
          </>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs font-medium text-gray-600">Content</label>
              <button type="button" onClick={() => fileRef.current?.click()} disabled={!articleId || uploading}
                title={!articleId ? 'Save the article first to add screenshots' : undefined}
                className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline disabled:opacity-40 disabled:no-underline">
                <ImageIcon size={13} /> {uploading ? 'Uploading…' : 'Insert screenshot'}
              </button>
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif" hidden onChange={onFile} />
            </div>
            <textarea value={body} onChange={e => setBody(e.target.value)} rows={12}
              className="w-full text-sm rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30" />
            <p className="text-xs text-gray-400 mt-1">Supports basic formatting — blank lines for paragraphs, "- " for bullets, "1. " for numbered steps.</p>
          </div>
        )}

        <label className="flex items-center gap-2 text-sm text-gray-600">
          <input type="checkbox" checked={visibility === 'public'} onChange={e => setVisibility(e.target.checked ? 'public' : 'internal')} />
          Publicly visible (otherwise internal only)
        </label>
      </div>
    </Modal>
  );
}

export default function KnowledgeBase() {
  const { user } = useAuth();
  const { articles, loading, search, setSearch, get, create, update, remove } = useKnowledgeBase();
  const canManage = hasRole(user, 'admin') || hasRole(user, 'it_manager');

  const [reading, setReading] = useState<KbArticle | null>(null);
  const [editing, setEditing] = useState<KbArticle | null>(null);
  const [creating, setCreating] = useState(false);

  const open = async (a: KbArticleSummary) => {
    const full = await get(a.id);
    if (full) setReading(full);
  };

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-navy">Knowledge Base</h1>
          <p className="text-sm text-gray-500">Guides and answers to common requests.</p>
        </div>
        {canManage && <Button onClick={() => setCreating(true)}><Plus size={15} /> New article</Button>}
      </div>

      <div className="relative max-w-md">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search articles…"
          className="w-full text-sm rounded-lg border border-gray-300 pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand" />
      </div>

      {loading ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">{[0, 1, 2].map(i => <SkeletonCard key={i} />)}</div>
      ) : articles.length === 0 ? (
        <Card><EmptyState icon={BookOpen} title="No articles found" description="Try a different search, or create the first article." /></Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {articles.map(a => (
            <button key={a.id} onClick={() => open(a)} className="text-left">
              <Card className="h-full hover:shadow-lg transition-shadow">
                {a.category && <span className="inline-block text-xs bg-brand/10 text-brand-dark rounded-full px-2 py-0.5 mb-2">{a.category}</span>}
                {a.resourceType === 'link' && (
                  <span className="inline-flex items-center gap-1 text-xs bg-violet-50 text-violet-700 rounded-full px-2 py-0.5 mb-2 ml-1">
                    <Link2 size={11} /> Link / video
                  </span>
                )}
                {!a.isPublished && <span className="inline-block text-xs bg-gray-100 text-gray-500 rounded-full px-2 py-0.5 mb-2 ml-1">draft</span>}
                <h3 className="text-md font-semibold text-navy flex items-center gap-1.5">
                  {a.resourceType === 'link' ? <Link2 size={15} className="text-gray-400" /> : <BookOpen size={15} className="text-gray-400" />}
                  {a.title}
                </h3>
                <p className="text-xs text-gray-400 mt-2 flex items-center gap-1"><Eye size={12} /> {a.views} views</p>
              </Card>
            </button>
          ))}
        </div>
      )}

      {/* Reading */}
      {reading && (
        <Modal open onClose={() => setReading(null)} size="lg" title={reading.title}
          footer={canManage ? <>
            <Button variant="ghost" onClick={() => { remove(reading.id); setReading(null); }}><Trash2 size={14} /> Delete</Button>
            <Button variant="secondary" onClick={() => { setEditing(reading); setReading(null); }}><Pencil size={14} /> Edit</Button>
          </> : undefined}>
          {reading.category && <span className="inline-block text-xs bg-brand/10 text-brand-dark rounded-full px-2 py-0.5 mb-3">{reading.category}</span>}
          {reading.resourceType === 'link' ? (
            <div className="space-y-3">
              {reading.body && (
                <div className="text-sm text-gray-700 leading-relaxed">
                  <ReactMarkdown components={mdComponents(reading.id)}>{reading.body}</ReactMarkdown>
                </div>
              )}
              {reading.externalUrl && (
                <a href={reading.externalUrl} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-dark bg-brand/10 hover:bg-brand/20 rounded-lg px-3 py-2 transition-colors">
                  <ExternalLink size={14} /> Open resource
                </a>
              )}
            </div>
          ) : (
            <div className="text-sm text-gray-700 leading-relaxed">
              <ReactMarkdown components={mdComponents(reading.id)}>{reading.body || ''}</ReactMarkdown>
            </div>
          )}
        </Modal>
      )}

      {creating && <Editor onClose={() => { setCreating(false); }} onCreate={create} onUpdate={update} />}
      {editing && <Editor initial={editing} onClose={() => setEditing(null)} onCreate={create} onUpdate={update} />}
    </div>
  );
}
